import { useState, useEffect } from 'react'

import { usePopper } from 'react-popper'
import classNames from 'classnames'
import Fade from 'components/misc/Fade'
import popperStyles from 'components/misc/styles/Tooltip.css'
import styles from './styles/FeedDropdown.css'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Link from '../misc/Link'
import { setNotificationItemsStatus } from '../../store/notifications/actions'
import CircleSpinner from '../misc/CircleSpinner'
import Header from '../typography/Header'
import formatDateString from 'lib/date-diff-string'

const NOTIFICATIONS_LIMIT = 99

const NotificationItem = ({ notification, history }) => (
  <div
    onClick={() => {
      if (notification.type === 'SCITE_WIDE_NOTIFICATION') {
        if (notification.digest?.link.includes('http')) {
          window.location.href = notification.digest?.link
        } else {
          history.push(notification.digest?.link)
        }
      } else {
        history.push('/users/me/feed?notificationIDs[0]=' + notification.notificationID)
      }
    }} className={classNames(styles.notificationItem, {
      [styles.new]: notification.status === 'NEW'
    })}
  >
    <div className={styles.notificationItemNewIndicator}>
      {notification.status === 'NEW' && (
        <div className={styles.notificationItemNewIndicatorDot} />
      )}
    </div>
    <div className={styles.notificationItemContent}>
      <div className={styles.notificationItemContentHeader}>
        <div className={styles.notificationItemContentHeaderIcon}>
          <img src={`${CONFIG.assetsBaseURL}/images/icons/feed-icons/${notification?.digest?.icon}.svg`} alt={notification?.digest?.title} />
        </div>
        <div className={styles.notificationItemContentHeaderTitle}>
          {notification?.digest?.title}
        </div>
      </div>
      <div className={styles.notificationItemBody}>
        {notification?.digest?.description}
      </div>
      <div className={styles.notificationItemTime}>
        {formatDateString(notification.createdAt)}
      </div>
    </div>
  </div>
)

const NoNotifications = () => (
  <div className={styles.noNotifications}>
    <Header level={6}>You don't have any notifications</Header>
    <div className={styles.noNotificationsIcon}>
      <img src={`${CONFIG.assetsBaseURL}/images/icons/largeBellIcon.svg`} />
    </div>
    <br />
    <div className={styles.noNotificationsText}>
      You don't have any notifications at the moment. Try setting some new ones!
      <br />
      <Link blue underline to='/users/me//notifications'>View Alert Settings</Link>
    </div>
  </div>
)

const FeedDropdown = ({ className, menuOffset = [-70, 12] }) => {
  const dispatch = useDispatch()
  const [referenceElement, setReferenceElement] = useState(null)
  const [popperElement, setPopperElement] = useState(null)
  const [arrowElement, setArrowElement] = useState(null)
  const [show, setShow] = useState(null)
  const history = useHistory()
  const notificationItemsLoading = useSelector(({ notifications }) => notifications.dropDownNotificationItemsLoading)

  // this assumes the API gives them in order of most recent first.
  const notificationItems = useSelector(({ notifications }) => notifications.dropDownNotificationItems)
  const newNotifications = useSelector(({ notifications }) => notifications.newNotificationItems)

  const handleShowMenu = e => {
    referenceElement.blur()

    if (show && newNotifications.length !== 0) {
      dispatch(setNotificationItemsStatus(
        notificationItems.filter(({ status }) => status === 'NEW').map(({ notificationID, id, type }) => ({ notificationID, id, type, status: 'SEEN' })))
      )
    }
    setShow(!show)
  }
  const handleWindowClick = e => {
    //
    // This is so you can only open one menu at a time
    //
    if (e.target.isSameNode(referenceElement) || (referenceElement && referenceElement.contains(e.target))) {
      return
    }

    if (show) {
      setShow(false)
    }
  }

  useEffect(() => {
    window.addEventListener('click', handleWindowClick)
    return () => window.removeEventListener('click', handleWindowClick)
  })

  const { styles: popperStyle, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: arrowElement
        }
      },
      {
        name: 'offset',
        options: {
          offset: menuOffset
        }
      }
    ]
  })

  return (
    <>
      <div
        className={classNames(styles.dropdown, className)}
        ref={setReferenceElement}
        onClick={handleShowMenu}
        tabIndex={0}
      >
        {newNotifications > 0 && (
          <div className={styles.newNotifications}>
            {Math.min(newNotifications, NOTIFICATIONS_LIMIT)}
          </div>
        )}
        <img
          className={styles.bellIcon} src={`${CONFIG.assetsBaseURL}/images/icons/bellIcon.svg`}
        />
      </div>
      <Fade show={show}>
        <div
          className={classNames(styles.menuLayout, popperStyles.popperBase)}
          onClick={handleShowMenu}
          ref={setPopperElement}
          style={popperStyle.popper}
          {...attributes.popper}
        >
          <div className={styles.notificationsContainer}>
            {notificationItemsLoading
              ? (
                <Fade show={notificationItemsLoading}>
                  <div className={styles.loadingPanel}>
                    <CircleSpinner size={100} />
                  </div>
                </Fade>
                )
              : (
                <Fade show={!notificationItemsLoading} className={styles.notificationInnerContainer}>
                  {notificationItems && notificationItems.length > 0
                    ? (
                      <div className={styles.notificationItemsContainer}>
                        <div className={styles.notificationItems}>
                          {notificationItems.map(notification => <NotificationItem dispatch={dispatch} notification={notification} key={`${notification.notificationID}-${notification.name}`} history={history} />)}
                        </div>
                        <div className={styles.viewAllNotifications}>
                          <Link blue underline to='/users/me/feed'>View All in Notification Feed</Link>
                        </div>
                      </div>
                      )
                    : (
                      <NoNotifications />
                      )}
                </Fade>
                )}
          </div>
          <div
            className={classNames(popperStyles.arrow, popperStyles.arrowWhite)}
            ref={setArrowElement}
            style={popperStyle.arrow}
          />
        </div>
      </Fade>
    </>
  )
}

export default FeedDropdown
